import { useMessageActionContext } from '@/app/providers';
import { CopyIcon, StarIcon, VideoIconFilled } from '@/assets/images';
import { toast } from 'react-toastify';
import { Checkbox, CustomButton } from '@/components';
import { handleJoinMeetLink } from '@/features/calendar/utils/meetLinkHelper';
import MessageActionDropdown from '@/features/chats/ui/Conversation/components/MessageActionDropdown';
import { isGroupChat } from '@/features/chats/utils/jidIdFormat';
import { useMessageAction } from '@/features/chats/viewmodels/useMessageAction';
import { formatChatTime, normalizeTimestamp } from '@/features/timeformatter/timeStamp';
import { Username, UserProfile } from '@/features/users';
import { useLanguage } from '@/localization/useLanguage';
import { blockOfflineAction, getLocalUserId, getMessageSenderId } from '@/shared/utils';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import MessageStatus from '../MessageStatus';
import ReplyMessageCard from '../ReplyMessageCard';
import './MeetCard.scss';

const SENDER_CLASS = 'sender-chat chat-bubble ct-flex ct-rowReverse ct-aiEnd ct-gap10 ct-padding-lr8-rl16';
const RECEIVER_CLASS = 'receiver-chat chat-bubble ct-flex ct-gap10 ct-padding-lr8-rl16';
const SENDER_INNER = 'ct-flex ct-flexColEnd ct-relative ct-gap4';
const RECEIVER_INNER = 'ct-flex ct-flexCol ct-relative ct-gap4';

const MeetCard = memo(({ message, activeChatJid }) => {
   const { t } = useLanguage();
   const { hideAvatar = false, hideActions = false } = useMessageActionContext();
   const { isSelected, isSelectionActive, handleToggleSelection } = useMessageAction(message, activeChatJid);
   const { msgBody = {}, timestamp: msgTimestamp, msgStatus } = message;
   const { title, link, scheduledDateTime, endTime } = msgBody?.meet || {};

   const senderId = getMessageSenderId({ type: 'chats', message });
   const isSender = senderId === getLocalUserId();

   const timestamp = useMemo(
      () => (msgTimestamp ? formatChatTime(new Date(Number(msgTimestamp)), 'message-card') : ''),
      [msgTimestamp],
   );
   const hideAvatarMeetCard = isSender || hideAvatar;
   const isChatGroup = useMemo(() => isGroupChat(activeChatJid), [activeChatJid]);
   const showProfile = isChatGroup && !hideAvatarMeetCard;

   const displayRange = useMemo(() => {
      if (!scheduledDateTime) return '';
      const start = DateTime.fromMillis(normalizeTimestamp(scheduledDateTime));
      const end = endTime ? DateTime.fromISO(endTime) : null;

      const formattedDate = start.toFormat('ccc, dd LLL');
      const startTimeText = start.toFormat('h:mm a');
      const endTimeText = end ? end.toFormat('h:mm a') : '';

      const timeRange = endTimeText ? ` - ${endTimeText}` : '';
      return `${formattedDate} at ${startTimeText}${timeRange}`;
   }, [scheduledDateTime, endTime]);

   const dispatch = useDispatch();

   const handleJoin = useCallback(() => {
      if (!link || blockOfflineAction()) return;
      handleJoinMeetLink(link, dispatch);
   }, [link, dispatch]);

   const handleCopyLink = useCallback(
      e => {
         e.stopPropagation();
         if (!link) return;
         navigator.clipboard.writeText(link).then(() => {
            toast.success(t('common.LINK_COPIED'));
         });
      },
      [link, t],
   );

   return (
      <CustomButton
         as="div"
         className={`message-row  custom-meet-card ${isSelectionActive && !hideActions ? 'ct-flex ct-aiCenter selection-active' : ''} ${isSelected && !hideActions ? 'selected' : ''} ${message.isHighlighted && !hideActions ? 'search-highlight-active' : ''}`}
         onClick={isSelectionActive && !hideActions ? handleToggleSelection : null}
      >
         {isSelectionActive && !hideActions && (
            <div className="selection-checkbox-wrap cts-meet">
               <Checkbox checked={isSelected} onChange={handleToggleSelection} />
            </div>
         )}
         <div className={`cts-meet ${isSender ? SENDER_CLASS : RECEIVER_CLASS}`}>
            {showProfile && (
               <picture className="chat-avatar cts-meet">
                  <UserProfile userId={senderId} className="chat-avatar chat-avatar-fallback ct-flexCenter" />
               </picture>
            )}
            <div
               className={`${isSender ? SENDER_INNER : RECEIVER_INNER} ${message.favouriteStatus === 1 ? 'starredMessage' : ''}`}
            >
               <div className="meet-card ct-flex ct-fcol">
                  {showProfile && <Username userId={senderId} />}
                  <ReplyMessageCard replyTo={msgBody?.replyTo} activeChatJid={activeChatJid} />
                  {/* Header */}
                  <div className="meet-card__header ct-flex ct-aiCenter ct-jcBetween">
                     <span className="meet-card__brand">{t('calendar.MEET_BRAND')}</span>
                     <div className="meet-card__brand-icon ct-flexCenter">
                        <VideoIconFilled />
                     </div>
                  </div>

                  {/* Body */}
                  <div className="meet-card__body ct-flex ct-fcol ct-gap12">
                     <h4 className="meet-card__title" title={title}>
                        {title}
                     </h4>
                     <p className="meet-card__datetime">{displayRange}</p>
                     {link && (
                        <div className="meet-card__link-wrapper ct-flex ct-aiCenter ct-jcBetween ct-gap8">
                           <p className="meet-card__link ct-ellipsis">{link}</p>
                           <CustomButton
                              as="div"
                              className="meet-card__copy-btn ct-flexCenter ct-cursor"
                              onClick={handleCopyLink}
                              title={t('common.COPY_LINK')}
                           >
                              <i className="ct-icon sm">
                                 <CopyIcon />
                              </i>
                           </CustomButton>
                        </div>
                     )}
                  </div>

                  {/* Actions */}
                  <div className="meet-card__actions ct-flex ct-aiCenter ct-gap12">
                     <button
                        className="meet-card__btn meet-card__btn--join ct-cursor ct-flex1 ct-flexCenter"
                        onClick={handleJoin}
                     >
                        {t('calendar.JOIN_MEETING')}
                     </button>
                  </div>
               </div>

               <div className={`ct-flexAlign ct-flex ct-gap8 ${isSender ? 'ct-rowReverse' : ''}`}>
                  {isSender && <MessageStatus msgStatus={msgStatus} />}
                  <span className="time cts-meet">{timestamp}</span>
                  {message.editedStatus === 1 && <span className="edited-icon">{t('message.EDITED')}</span>}
                  {message.favouriteStatus === 1 && (
                     <i className="icon starIcon ct-flexCenter">
                        <StarIcon />
                     </i>
                  )}
               </div>

               {!hideActions && (
                  <MessageActionDropdown
                     className="cts-meet"
                     message={message}
                     isSender={isSender}
                     activeChatJid={activeChatJid}
                  />
               )}
            </div>
         </div>
      </CustomButton>
   );
});

MeetCard.propTypes = {
   message: PropTypes.object.isRequired,
   activeChatJid: PropTypes.string,
};

export default MeetCard;
